import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { ReservationSlotTemplate } from '../../../../domain/reservationSlots';
import type { UseReservationSlotSettingsReturn } from '../hooks/useReservationSlotSettings';
import type { UseReservationSlotGenerationReturn } from '../hooks/useReservationSlotGeneration';
import { formatYearMonthToSlash } from '../../../formatters';
import SetupHowGuide from './SetupHowGuide';
import SetupStepHeader from './SetupStepHeader';
import TemplateCard from './TemplateCard';
import type { TemplateCardBadge } from './TemplateCard';
import SettingsSummaryBlocks from './SettingsSummaryBlocks';
import GenerationPanel from './GenerationPanel';
import { BackNav, TemplateCardGrid, Callout } from '../ReservationSlotManagement.styles';

/**
 * SetupView のProps型
 *
 * @property {number} year - 設定対象の年
 * @property {number} month - 設定対象の月（1-12）
 * @property {string} originLabel - 遷移元タブの名称（戻るボタンに表示）
 * @property {ReservationSlotTemplate[]} templates - テンプレート一覧
 * @property {UseReservationSlotSettingsReturn} settings - 基本設定フックの戻り値
 * @property {UseReservationSlotGenerationReturn} generation - 解放枠生成フックの戻り値
 * @property {function} onBack - 遷移元タブへ戻る
 * @property {function} onOpenTemplates - 「基本設定テンプレート」タブへ移動する
 * @property {function} onEditTemplate - テンプレート編集ダイアログを開く
 * @property {function} onOpenSettings - 基本設定ダイアログを開く
 */
export interface SetupViewProps {
  year: number;
  month: number;
  originLabel: string;
  templates: ReservationSlotTemplate[];
  settings: UseReservationSlotSettingsReturn;
  generation: UseReservationSlotGenerationReturn;
  onBack: () => void;
  onOpenTemplates: () => void;
  onEditTemplate: (template: ReservationSlotTemplate) => void;
  onOpenSettings: () => void;
}

/**
 * テンプレートに付ける適用状況バッジを判定する
 *
 * @description
 * デフォルトテンプレートには「基本設定」、個別上書きから参照されているテンプレートには
 * 「個別上書き」のバッジを付ける。どちらでもないテンプレートはバッジなし。
 * デフォルトかつ上書きからも参照されている場合は、影響範囲の広い「基本設定」を優先する。
 *
 * @param {ReservationSlotTemplate} template - 判定対象のテンプレート
 * @param {string | null} defaultTemplateId - デフォルトテンプレートID
 * @param {readonly { templateId: string }[]} overrides - 医師個別の上書き設定
 * @returns {TemplateCardBadge | null} 付与するバッジ種別（不要な場合はnull）
 */
function resolveTemplateBadge(
  template: ReservationSlotTemplate,
  defaultTemplateId: string | null,
  overrides: readonly { templateId: string }[]
): TemplateCardBadge | null {
  if (template.id === defaultTemplateId) {
    return 'default';
  }
  if (overrides.some((override) => override.templateId === template.id)) {
    return 'override';
  }
  return null;
}

/**
 * 月スコープの解放枠設定ビュー
 *
 * @description
 * 「確定済みシフト」または「解放枠一覧」から特定の月を選んだときに開く設定画面。
 * STEP 1（テンプレート確認）→ STEP 2（基本設定・個別上書き確認）→ STEP 3（一括生成）の
 * 3ステップで構成し、対象月は遷移元で確定しているため月の切り替えは行わない。
 *
 * @param {SetupViewProps} props - コンポーネントProps
 * @returns {React.ReactElement} 解放枠設定ビュー
 */
const SetupView: React.FC<SetupViewProps> = ({
  year,
  month,
  originLabel,
  templates,
  settings,
  generation,
  onBack,
  onOpenTemplates,
  onEditTemplate,
  onOpenSettings,
}) => {
  const yearMonthLabel = formatYearMonthToSlash(year, month);

  return (
    <Box>
      <BackNav>
        <Button onClick={onBack} size="small" sx={{ p: 0, minWidth: 0, fontWeight: 600 }}>
          ← {originLabel}
        </Button>
        <Typography component="span" variant="body2" color="text.secondary">
          /
        </Typography>
        <Typography component="span" variant="body2" fontWeight={700}>
          {yearMonthLabel}
        </Typography>
      </BackNav>

      <Typography variant="h5" component="h2" fontWeight={600} sx={{ mb: 2.5 }}>
        {yearMonthLabel} 解放枠設定
      </Typography>

      <SetupHowGuide />

      {/* STEP 1: テンプレートの確認 */}
      <SetupStepHeader
        step={1}
        title="テンプレートを確認する"
        description="「基本設定テンプレート」タブで作成・編集できます"
        action={
          <Button size="small" variant="outlined" onClick={onOpenTemplates}>
            テンプレートを確認 →
          </Button>
        }
      />
      {templates.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          テンプレートが登録されていません。「基本設定テンプレート」タブから追加してください。
        </Typography>
      ) : (
        <TemplateCardGrid>
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              badge={resolveTemplateBadge(template, settings.defaultTemplateId, settings.overrides)}
              onEdit={onEditTemplate}
            />
          ))}
        </TemplateCardGrid>
      )}

      {/* STEP 2: 基本設定・個別上書きの確認 */}
      <SetupStepHeader
        step={2}
        title="基本設定と個別上書きを確認する"
        description="「基本設定」タブで変更できます"
        action={
          <Button size="small" variant="outlined" onClick={onOpenSettings}>
            基本設定を確認 →
          </Button>
        }
      />
      <SettingsSummaryBlocks
        templates={templates}
        defaultTemplateId={settings.defaultTemplateId}
        overrides={settings.overrides}
      />

      {/* STEP 3: 内容確認と一括生成 */}
      <SetupStepHeader
        step={3}
        title="内容を確認して一括生成する"
        description="自動割り当てを確認し、必要な行だけ変更してください"
      />
      <Callout $tone="blue" sx={{ mb: 2 }}>
        <Box component="span" sx={{ flexShrink: 0, fontWeight: 800 }}>
          ⓘ
        </Box>
        <Box component="span">
          <strong>基本設定</strong>・<strong>個別上書き</strong>・<strong>手動変更</strong>
          バッジで適用元がわかります。変更したい行のみ上書きしてください。
        </Box>
      </Callout>

      <GenerationPanel
        templates={templates}
        generation={generation}
        embedded
        onCancel={onBack}
      />
    </Box>
  );
};

SetupView.displayName = 'SetupView';

export default SetupView;
